import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

export default function SupabaseDebug() {
  const [logs, setLogs] = useState<string[]>([]);
  const [savedValue, setSavedValue] = useState('');
  const [testValue, setTestValue] = useState('Test value ' + new Date().toISOString());
  const [userInfo, setUserInfo] = useState<any>(null);
  const [schemaInfo, setSchemaInfo] = useState<any>(null);
  const [rpcInfo, setRpcInfo] = useState<any>(null);
  const [directAccessInfo, setDirectAccessInfo] = useState<any>(null);
  const [manualInsertResult, setManualInsertResult] = useState<any>(null);

  useEffect(() => {
    // Check user info and role
    checkUserInfo();
  }, []);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const checkUserInfo = async () => {
    try {
      addLog('Checking user info...');
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        addLog(`Error getting user: ${userError.message}`);
        return;
      }
      
      if (!userData?.user) {
        addLog('No authenticated user found');
        return;
      }
      
      // Get user role
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userData.user.id)
        .single();
        
      if (profileError) {
        addLog(`Error getting profile: ${profileError.message}`);
        return;
      }
      
      const info = {
        id: userData.user.id,
        email: userData.user.email,
        role: profileData?.role || 'unknown'
      };
      
      setUserInfo(info);
      addLog(`User info: ${JSON.stringify(info)}`);
    } catch (err) {
      addLog(`Exception in checkUserInfo: ${err}`);
    }
  };
  
  const checkSchema = async () => {
    try {
      addLog('Checking app_settings schema...');
      const schemaExists = await checkIfSchemaExists();
      
      addLog(`Schema check result: ${JSON.stringify(schemaExists)}`);
      setSchemaInfo(schemaExists);
    } catch (err) {
      addLog(`Exception in checkSchema: ${err}`);
    }
  };
  
  const checkIfSchemaExists = async () => {
    // Check if create_app_settings_if_not_exists function exists
    const { data: functionData, error: functionError } = await supabase
      .from('pg_proc')
      .select('proname')
      .eq('proname', 'create_app_settings_if_not_exists')
      .single();
      
    if (functionError) {
      return { 
        functionExists: false, 
        functionError: functionError.message 
      };
    }
    
    // Call the function
    const { data: callData, error: callError } = await supabase
      .rpc('create_app_settings_if_not_exists');
      
    if (callError) {
      return { 
        functionExists: true, 
        functionCallError: callError.message 
      };
    }
    
    // Check if app_settings schema exists
    try {
      const { data: schemaData, error: schemaError } = await supabase
        .rpc('check_schema_exists', { schema_name: 'app_settings' });
        
      if (schemaError || !schemaData) {
        // Fall back to pg_namespace query
        const { data: nsData, error: nsError } = await supabase
          .from('pg_namespace')
          .select('nspname')
          .eq('nspname', 'app_settings')
          .single();
          
        if (nsError) {
          return {
            functionExists: true,
            functionCallSuccess: !callError,
            schemaExists: false,
            schemaError: nsError.message
          };
        }
        
        return {
          functionExists: true,
          functionCallSuccess: !callError,
          schemaExists: !!nsData,
          schemaData: nsData
        };
      }
      
      return {
        functionExists: true,
        functionCallSuccess: !callError,
        schemaExists: !!schemaData,
        schemaData
      };
    } catch (err) {
      return {
        functionExists: true,
        functionCallSuccess: !callError,
        error: err.message
      };
    }
  };
  
  const testRPC = async () => {
    try {
      addLog('Testing set_setting RPC function...');
      
      // First, get current value
      const { data: currentData, error: currentError } = await supabase
        .rpc('get_setting', { p_key: 'debug_test_setting' });
        
      addLog(`Current value: ${currentData || '(none)'}, Error: ${currentError?.message || 'none'}`);
      
      // Now set a new value
      const newValue = testValue;
      const { data: setData, error: setError } = await supabase
        .rpc('set_setting', { 
          p_key: 'debug_test_setting',
          p_value: newValue,
          p_description: 'Debug test setting'
        });
        
      // Log raw RPC response for analysis
      addLog(`RPC set_setting raw response: ${JSON.stringify({ data: setData, error: setError })}`);
      
      if (setError) {
        addLog(`Error setting value: ${setError.message}`);
        setRpcInfo({
          success: false,
          error: setError.message,
          data: setData
        });
        return;
      }
      
      // Get the value again to verify
      const { data: verifyData, error: verifyError } = await supabase
        .rpc('get_setting', { p_key: 'debug_test_setting' });
        
      if (verifyError) {
        addLog(`Error verifying value: ${verifyError.message}`);
        setRpcInfo({
          success: false,
          setOk: true,
          getError: verifyError.message,
          data: setData
        });
        return;
      }
      
      // Check if value matches
      const success = verifyData === newValue;
      addLog(`Verification: ${success ? 'SUCCESS' : 'FAILED'}, Expected: "${newValue}", Got: "${verifyData}"`);
      
      setSavedValue(verifyData || '');
      setRpcInfo({
        success,
        expected: newValue,
        actual: verifyData,
        data: setData
      });
    } catch (err) {
      addLog(`Exception in testRPC: ${err}`);
      setRpcInfo({
        success: false,
        exception: err.message
      });
    }
  };
  
  const checkDirectAccess = async () => {
    try {
      addLog('Checking direct access to app_settings.settings...');
      
      // Try SELECT with rpc and proper syntax (most reliable)
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_settings_direct');
        
      // If rpc doesn't exist, try direct table access
      let rawData: any = null;
      let rawError: any = null;
      
      try {
        const { data, error } = await supabase
          .from('app_settings.settings')
          .select('*');
          
        rawData = data;
        rawError = error;
      } catch (directErr: any) {
        rawError = directErr;
      }
      
      setDirectAccessInfo({
        rpcResult: {
          data: rpcData,
          error: rpcError?.message
        },
        directResult: {
          data: rawData,
          error: rawError?.message
        }
      });
      
      if (rpcData) {
        addLog(`Direct access via RPC success: ${(rpcData as any[]).length} rows found`);
      } else if (rpcError) {
        addLog(`Direct access via RPC failed: ${rpcError.message}`);
      }
      
      if (rawData) {
        addLog(`Direct table access success: ${rawData.length} rows found`);
      } else if (rawError) {
        addLog(`Direct table access failed: ${rawError.message}`);
      }
    } catch (err: any) {
      addLog(`Exception in checkDirectAccess: ${err.message}`);
      setDirectAccessInfo({
        exception: err.message
      });
    }
  };
  
  const tryManualInsert = async () => {
    try {
      addLog('Trying manual SQL insert...');
      
      // Try to insert directly using execute_sql RPC
      const { data, error } = await supabase
        .rpc('execute_sql', { 
          sql: `
            INSERT INTO app_settings.settings (key, value, description, updated_at)
            VALUES ('debug_manual_test', '${testValue}', 'Manual test value', now())
            ON CONFLICT (key) DO UPDATE
            SET value = EXCLUDED.value,
                description = EXCLUDED.description,
                updated_at = EXCLUDED.updated_at
            RETURNING *;
          `
        });
        
      if (error) {
        addLog(`Manual insert error: ${error.message}`);
        setManualInsertResult({
          success: false,
          error: error.message
        });
        return;
      }
      
      addLog(`Manual insert success: ${JSON.stringify(data)}`);
      setManualInsertResult({
        success: true,
        data
      });
      
      // Verify with get_setting
      const { data: verifyData, error: verifyError } = await supabase
        .rpc('get_setting', { p_key: 'debug_manual_test' });
        
      if (verifyError) {
        addLog(`Verification error: ${verifyError.message}`);
        return;
      }
      
      addLog(`Verification: Value = "${verifyData}"`);
    } catch (err) {
      addLog(`Exception in tryManualInsert: ${err}`);
      setManualInsertResult({
        success: false,
        exception: err.message
      });
    }
  };
  
  const createRPCFunctions = async () => {
    try {
      addLog('Creating helper RPC functions...');
      
      // Create check_schema_exists function
      const schemaCheck = await supabase
        .rpc('execute_sql', {
          sql: `
            CREATE OR REPLACE FUNCTION public.check_schema_exists(schema_name text)
            RETURNS boolean
            LANGUAGE plpgsql
            SECURITY DEFINER
            AS $$
            DECLARE
              schema_exists boolean;
            BEGIN
              SELECT EXISTS(
                SELECT 1 FROM pg_namespace WHERE nspname = schema_name
              ) INTO schema_exists;
              
              RETURN schema_exists;
            END;
            $$;
            
            GRANT EXECUTE ON FUNCTION public.check_schema_exists TO authenticated;
          `
        });
        
      if (schemaCheck.error) {
        addLog(`Error creating check_schema_exists: ${schemaCheck.error.message}`);
      } else {
        addLog('Created check_schema_exists function');
      }
      
      // Create get_settings_direct function
      const getSettingsDirect = await supabase
        .rpc('execute_sql', {
          sql: `
            CREATE OR REPLACE FUNCTION public.get_settings_direct()
            RETURNS SETOF app_settings.settings
            LANGUAGE plpgsql
            SECURITY DEFINER
            AS $$
            BEGIN
              RETURN QUERY SELECT * FROM app_settings.settings;
            END;
            $$;
            
            GRANT EXECUTE ON FUNCTION public.get_settings_direct TO authenticated;
          `
        });
        
      if (getSettingsDirect.error) {
        addLog(`Error creating get_settings_direct: ${getSettingsDirect.error.message}`);
      } else {
        addLog('Created get_settings_direct function');
      }
      
      // Create execute_sql function if it doesn't exist
      const executeSql = await supabase
        .rpc('execute_sql', {
          sql: `
            DO $$
            BEGIN
              IF NOT EXISTS (
                SELECT 1 FROM pg_proc WHERE proname = 'execute_sql' AND pronamespace = (SELECT oid FROM pg_namespace WHERE nspname = 'public')
              ) THEN
                CREATE OR REPLACE FUNCTION public.execute_sql(sql text)
                RETURNS JSONB
                LANGUAGE plpgsql
                SECURITY DEFINER
                AS $$
                DECLARE
                  result JSONB;
                BEGIN
                  EXECUTE sql INTO result;
                  RETURN result;
                EXCEPTION WHEN OTHERS THEN
                  RETURN jsonb_build_object('error', SQLERRM, 'detail', SQLSTATE);
                END;
                $$;
                
                GRANT EXECUTE ON FUNCTION public.execute_sql TO authenticated;
              END IF;
            END$$;
          `
        });
        
      if (executeSql.error) {
        addLog(`Note: execute_sql already exists: ${executeSql.error.message}`);
      } else {
        addLog('Created/verified execute_sql function');
      }
      
    } catch (err) {
      addLog(`Exception in createRPCFunctions: ${err}`);
    }
  };
  
  const recreateAppSettings = async () => {
    if (!confirm('This will recreate the app_settings schema and tables. Continue?')) {
      return;
    }
    
    try {
      addLog('Recreating app_settings schema and tables...');
      
      // Call the create_app_settings_if_not_exists function
      const { data, error } = await supabase
        .rpc('create_app_settings_if_not_exists');
        
      if (error) {
        addLog(`Error recreating app_settings: ${error.message}`);
        return;
      }
      
      addLog('Successfully recreated app_settings');
      
      // Re-run schema check
      await checkSchema();
      
      // Try to save a value 
      await testRPC();
    } catch (err) {
      addLog(`Exception in recreateAppSettings: ${err}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Supabase Debug Tools</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column - Controls */}
        <div className="space-y-6">
          {/* User info */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">User Information</h2>
            <div className="mb-4">
              <button 
                onClick={checkUserInfo}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Check User Info
              </button>
            </div>
            {userInfo && (
              <div className="mt-2">
                <div className="bg-gray-100 p-3 rounded">
                  <p><strong>User ID:</strong> {userInfo.id}</p>
                  <p><strong>Email:</strong> {userInfo.email}</p>
                  <p><strong>Role:</strong> {userInfo.role}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Create RPC functions */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Setup Helper Functions</h2>
            <button 
              onClick={createRPCFunctions}
              className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
            >
              Create Helper Functions
            </button>
          </div>
          
          {/* Schema check */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Schema Check</h2>
            <div className="mb-4">
              <button 
                onClick={checkSchema}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Check Schema
              </button>
            </div>
            {schemaInfo && (
              <div className="mt-2">
                <div className="bg-gray-100 p-3 rounded">
                  <pre className="text-xs whitespace-pre-wrap">
                    {JSON.stringify(schemaInfo, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
          
          {/* RPC Test */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Test RPC Functions</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Test Value</label>
              <input 
                type="text"
                value={testValue}
                onChange={(e) => setTestValue(e.target.value)}
                className="w-full border rounded p-2 mb-3"
              />
              <button 
                onClick={testRPC}
                className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600"
              >
                Test set_setting RPC
              </button>
            </div>
            
            {savedValue && (
              <div className="mt-2">
                <p><strong>Saved Value:</strong> {savedValue}</p>
              </div>
            )}
            
            {rpcInfo && (
              <div className="mt-2">
                <div className="bg-gray-100 p-3 rounded">
                  <pre className="text-xs whitespace-pre-wrap">
                    {JSON.stringify(rpcInfo, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
          
          {/* Direct Access */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Check Direct Table Access</h2>
            <div className="mb-4">
              <button 
                onClick={checkDirectAccess}
                className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
              >
                Check Direct Access
              </button>
            </div>
            
            {directAccessInfo && (
              <div className="mt-2">
                <div className="bg-gray-100 p-3 rounded">
                  <pre className="text-xs whitespace-pre-wrap">
                    {JSON.stringify(directAccessInfo, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
          
          {/* Manual SQL Insert */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Try Manual SQL Insert</h2>
            <div className="mb-4">
              <button 
                onClick={tryManualInsert}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Run Manual SQL Insert
              </button>
            </div>
            
            {manualInsertResult && (
              <div className="mt-2">
                <div className="bg-gray-100 p-3 rounded">
                  <pre className="text-xs whitespace-pre-wrap">
                    {JSON.stringify(manualInsertResult, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
          
          {/* Recreate Schema */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Recreate app_settings Schema</h2>
            <div className="mb-4">
              <button 
                onClick={recreateAppSettings}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Recreate app_settings Schema
              </button>
              <p className="text-sm text-gray-500 mt-2">
                Warning: This will run the create_app_settings_if_not_exists function
              </p>
            </div>
          </div>
        </div>
        
        {/* Right column - Logs */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Debug Logs</h2>
            <div className="bg-black text-green-400 p-4 rounded h-[800px] overflow-y-auto font-mono text-sm">
              {logs.map((log, i) => (
                <div key={i} className="mb-1">{log}</div>
              ))}
              {logs.length === 0 && (
                <div className="text-gray-500 italic">No logs yet. Run some tests...</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 